/* eslint-disable no-console */
import {
  useVerificationData,
} from '../utils';
import authApi from 'api/auth';
import { actionCreator } from 'api/utils';
import {
  LoginVerificationMethod } from 'components';
import { Button } from 'components/InteractiveUIControls/Button/Button';
import { useAuthCheck,
  useDocumentTitle } from 'hooks';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { notificationsService } from 'services';
import { rootActionTypes } from 'store/actions';
import { CURRENT_CHANNEL_MAP, VerificationChannel } from 'types';

export const ResendVerification = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [
    { channel,
      to },
    setVerificationData,
  ] = useVerificationData();
  const [
    isLoading,
    setIsLoading,
  ] = useState<boolean>(false);
  const [methodSelected, setMethodSelected] = useState<Map<boolean, VerificationChannel> | null>(new Map());

  useAuthCheck();
  useDocumentTitle('Resend Code');

  const phone = useSelector<{ auth: { phone: string | undefined, }, }>(
    (state) => state.auth.phone,
  ) as string | undefined ||
  sessionStorage.getItem('phone') ||
  '';

  const getUpdatedChannelMap = (): typeof CURRENT_CHANNEL_MAP => {
    const updatedMap = new Map(CURRENT_CHANNEL_MAP);

    const setEnabled = (chan: VerificationChannel, enabled: boolean) => {
      const current = updatedMap.get(channel);
      if (current) {
        updatedMap.set(chan, { ...current, enabled });
      }
    };

    if (phone && (channel === VerificationChannel.Sms || channel === VerificationChannel.WhatsApp)) {
      setEnabled(VerificationChannel.Sms, true);
      setEnabled(VerificationChannel.WhatsApp, true);
      updatedMap.delete(VerificationChannel.Email);
    }

    if (phone && channel === VerificationChannel.Email) {
      setEnabled(VerificationChannel.Email, true);
      setEnabled(VerificationChannel.Sms, true);
      setEnabled(VerificationChannel.WhatsApp, true);
    }

    if (!phone) {
      setEnabled(VerificationChannel.Email, true);
      setEnabled(VerificationChannel.Sms, false);
      setEnabled(VerificationChannel.WhatsApp, false);
    }

    return updatedMap;
  };

  const onMethodChange = async (channelToResend: VerificationChannel) => {
    setIsLoading(true);
    try {
      await authApi.resendVerification({ channel: channelToResend });
      notificationsService.success('Code resent.');
      setVerificationData(to, channelToResend);
      navigate(-1);
    } catch {
      notificationsService.error('Something went wrong, please try to login again.');
      navigate('/auth/login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate('/auth/login');
    localStorage.clear();
    sessionStorage.clear();
    dispatch(actionCreator(rootActionTypes.CLEAR_STORE, {}));
  };

  return <div className='flex flex-col gap-0.5'>
    <div className='flex flex-col gap-0.5'>
      <Button
        className='!pr-[0.75rem]'
        data-test='click-back-button-resend-verification'
        icon='left'
        iconid='UP-arrow-left-1f'
        onClick={() => handleBackClick()}
        squared
        text='Back'
        tooltipWrapperClass='!w-auto !justify-start absolute top-[2rem]'
        type='outline-grey'
      />
      {<div className='dt:large-title text-center text-title-1 font-medium dt:text-left'>
        Choose how you want to sign in:
      </div>}
      <span className='text-center text-title-4 text-black-700 dt:text-left dt:text-font-1'>
        A verification code will be sent to your selected option
      </span>
    </div>
    <div className='h-6' />
    <div className='flex flex-col gap-0.5 px-1 dt:px-0'>
      {Array.from(getUpdatedChannelMap().entries()).map(([type, channelData]) => <div className='flex flex-col gap-1' key={type}>
        <LoginVerificationMethod
          activeType={channel}
          enabled={channelData.enabled}
          methodSelected={methodSelected}
          setMethodSelected={setMethodSelected}
          type={type as VerificationChannel}
        />
      </div>,
      )}
    </div>
    <Button className='!h-11 w-full' isLoading={isLoading} onClick={() => onMethodChange(methodSelected?.get(true) as VerificationChannel)} size='bigger-medium' squared text='Send' type='green' />
  </div>;
};
